import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { 
  CustomerPermissions, 
  CustomerDashboardSection, 
  CustomerPermissionAction,
  DEFAULT_CUSTOMER_PERMISSIONS 
} from '@/types/permissions';
import { isActionEnabled, isSectionEnabled } from '@/lib/permissions/customerPermissions';
import { 
  getUserEffectivePermissions,
  getUserEffectivePermissionsInOrganization,
  userHasSectionPermission,
  userHasActionPermission 
} from '@/lib/firebase/queries/permissions';

interface UseCustomerPermissionsOptions {
  /**
   * When false, resolution waits (loading stays true). Used while the active
   * organization is still loading so we never resolve without the org.
   */
  enabled?: boolean;
}

export function useCustomerPermissions(
  organizationId?: string,
  { enabled = true }: UseCustomerPermissionsOptions = {}
) {
  const { currentUser } = useAuth();
  const [permissions, setPermissions] = useState<CustomerPermissions>(DEFAULT_CUSTOMER_PERMISSIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Which user + organization the permissions belong to: a change shows as
  // loading in the same render, not one render later (see useOrganization).
  const requestKey = `${currentUser?.uid ?? ''}|${organizationId ?? ''}`;
  const [resolvedKey, setResolvedKey] = useState<string | null>(null);

  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      requestIdRef.current++;
      setLoading(true);
      return;
    }
    if (currentUser) {
      loadPermissions();
    } else {
      requestIdRef.current++;
      setPermissions(DEFAULT_CUSTOMER_PERMISSIONS);
      setLoading(false);
      setResolvedKey(requestKey);
    }
    // The uid, not the user object: AuthContext hands out a new object when it
    // refreshes, and reloading here flipped `loading` back on — the customer
    // route guard then unmounted the open page and it loaded again from scratch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, organizationId, enabled]);

  const loadPermissions = async () => {
    if (!currentUser) return;
    const requestId = ++requestIdRef.current;

    try {
      setLoading(true);
      setError(null);
      
        let userPermissions: CustomerPermissions;
        
        if (organizationId) {
          // Get permissions for specific organization context
          userPermissions = await getUserEffectivePermissionsInOrganization(
            currentUser.uid, 
            organizationId
          );
        } else {
          // Get general effective permissions
          userPermissions = await getUserEffectivePermissions(currentUser.uid);
        }
        
        // Ignore stale results after an organization switch.
        if (requestId !== requestIdRef.current) return;
        setPermissions(userPermissions);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error loading user permissions:', err);
      setError('Failed to load permissions');
      // Fallback to default permissions on error
      setPermissions(DEFAULT_CUSTOMER_PERMISSIONS);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setResolvedKey(requestKey);
      }
    }
  };

  const hasSectionAccess = (section: CustomerDashboardSection): boolean => {
    // Mobile semantics: only an explicit false hides a section.
    return isSectionEnabled(permissions, section);
  };

  const hasActionPermission = (action: CustomerPermissionAction): boolean => {
    return isActionEnabled(permissions, action);
  };

  // Async version for real-time checks
  const checkSectionPermission = async (section: CustomerDashboardSection): Promise<boolean> => {
    if (!currentUser) return true; // Default to allowing access if no user
    
    try {
      return await userHasSectionPermission(currentUser.uid, section);
    } catch (error) {
      console.error('Error checking section permission:', error);
      return true; // Default to allowing access on error
    }
  };

  const checkActionPermission = async (action: CustomerPermissionAction): Promise<boolean> => {
    if (!currentUser) return true; // Default to allowing access if no user
    
    try {
      return await userHasActionPermission(currentUser.uid, action);
    } catch (error) {
      console.error('Error checking action permission:', error);
      return true; // Default to allowing access on error
    }
  };

  return {
    permissions,
    loading: loading || resolvedKey !== requestKey,
    error,
    hasSectionAccess,
    hasActionPermission,
    checkSectionPermission,
    checkActionPermission,
    reload: loadPermissions,
  };
}